import React, { useState, useEffect, useRef } from 'react';

const GSI_SCRIPT_URL = "https://accounts.google.com/gsi/client";
function useLoadGsiScript(options = {}) {
    const { onLoadScriptError, gsiSrc = GSI_SCRIPT_URL } = options;
    const [scriptLoadedSuccessfully, setScriptLoadedSuccessfully] = useState(false);
    useEffect(() => {
        const scriptTag = document.createElement("script");
        scriptTag.src = gsiSrc;
        scriptTag.async = true;
        scriptTag.defer = true;
        scriptTag.onload = () => {
            setScriptLoadedSuccessfully(true);
        };
        scriptTag.onerror = () => {
            setScriptLoadedSuccessfully(false);
            onLoadScriptError === null || onLoadScriptError === void 0 ? void 0 : onLoadScriptError(new Error("Cannot load GSI script!"));
        };
        document.body.appendChild(scriptTag);
        return () => {
            document.body.removeChild(scriptTag);
        };
    }, []);
    return scriptLoadedSuccessfully;
}

const containerHeightMap = { large: 40, medium: 32, small: 20 };
function GoogleBuiltInLoginButton(props) {
    const { clientId, mode, gsiSrc, useOneTap, idConfiguration, locale, logo_alignment, shape, size, text, theme, type, width, onLoadScriptError, } = props;
    // 1. Load GSI script
    const scriptLoaded = useLoadGsiScript({ onLoadScriptError, gsiSrc });
    const btnContainerRef = useRef(null);
    const handleCallback = (credentialResponse, onSuccess, onError) => {
        if (credentialResponse.credential) {
            onSuccess === null || onSuccess === void 0 ? void 0 : onSuccess(credentialResponse);
            return;
        }
        onError === null || onError === void 0 ? void 0 : onError();
    };
    useEffect(() => {
        var _a, _b, _c;
        if (!scriptLoaded)
            return;
        const idConfig = mode === "popup"
            ? {
                ...idConfiguration,
                client_id: clientId,
                ux_mode: "popup",
                callback: res => handleCallback(res, props.onSuccess, props.onError),
            }
            : {
                ...idConfiguration,
                client_id: clientId,
                ux_mode: "redirect",
                login_uri: props.redirectUrl,
                ...(useOneTap && {
                    callback: res => handleCallback(res, props.onSuccess, props.onError),
                }),
            };
        // 2. initialize the account id
        (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.id.initialize(idConfig);
        // 3. render built-in button
        (_b = window.google) === null || _b === void 0 ? void 0 : _b.accounts.id.renderButton(btnContainerRef.current, {
            type: type || "standard",
            theme: theme || "outline",
            size: size || "medium",
            shape: shape || "rectangular",
            logo_alignment: logo_alignment || "left",
            locale,
            text,
            width,
        });
        // 4. if one tap enable, register prompt handler
        if (useOneTap) {
            (_c = window.google) === null || _c === void 0 ? void 0 : _c.accounts.id.prompt(props.momentListener);
        }
        // 5. cancel account state when one tap enabled
        return () => {
            var _a;
            if (useOneTap)
                (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.id.cancel();
        };
    });
    return React.createElement("div", { ref: btnContainerRef, style: { height: containerHeightMap[size || "medium"] } });
}

const OAUTH_USER_INFO_URL = "https://www.googleapis.com/oauth2/v3/userinfo";
async function getUserInfos(token, endpoint) {
    try {
        const res = await fetch(`${endpoint || OAUTH_USER_INFO_URL}?access_token=${token.access_token}`);
        const userInfo = (await res.json());
        return userInfo;
    }
    catch {
        return null;
    }
}

function GoogleCustomLoginButton(props) {
    const { tag = "div", className, children, onLoadScriptError, gsiSrc, clientId, scope = "openid profile email", onNonOAuthError, onError, enable_serial_consent, hint, hosted_domain, include_granted_scopes, state, } = props;
    // 1. Load GSI script
    const scriptLoaded = useLoadGsiScript({ gsiSrc, onLoadScriptError });
    const clientRef = useRef();
    useEffect(() => {
        var _a, _b;
        if (!scriptLoaded)
            return;
        // 2. Initialize the client
        const clientConfig = {
            client_id: clientId,
            scope,
            error_callback: onNonOAuthError,
            enable_serial_consent,
            hint,
            hosted_domain,
            state,
            include_granted_scopes,
        };
        if (props.responseType === "token") {
            clientRef.current = (_a = window.google) === null || _a === void 0 ? void 0 : _a.accounts.oauth2.initTokenClient({
                ...clientConfig,
                prompt: props.prompt,
                callback: async (response) => {
                    var _a;
                    if ("error" in response) {
                        return onError === null || onError === void 0 ? void 0 : onError(response);
                    }
                    if ("access_token" in response) {
                        const userInfo = await getUserInfos(response, props.userInfoEndpoint);
                        if (userInfo) {
                            (_a = props.onSuccess) === null || _a === void 0 ? void 0 : _a.call(props, Object.assign({}, userInfo, response));
                        }
                        else {
                            onError === null || onError === void 0 ? void 0 : onError({ error: "Cannot retrieve user info" });
                        }
                    }
                },
            });
        }
        else if (props.responseType === "code") {
            clientRef.current = (_b = window.google) === null || _b === void 0 ? void 0 : _b.accounts.oauth2.initCodeClient({
                ...clientConfig,
                redirect_uri: props.redirect_uri,
                select_account: props.select_account,
                ux_mode: props.ux_mode,
                callback: async (response) => {
                    var _a;
                    if ("error" in response) {
                        return onError === null || onError === void 0 ? void 0 : onError(response);
                    }
                    if ("code" in response) {
                        (_a = props.onSuccess) === null || _a === void 0 ? void 0 : _a.call(props, response);
                    }
                },
            });
        }
    });
    // 3. Request token when user manually clicks
    const handleOnLogin = () => {
        if (props.responseType === "token") {
            const overrideConfig = {
                scope,
                enable_serial_consent,
                hint,
                include_granted_scopes,
                prompt: props.prompt,
                state,
            };
            return clientRef.current.requestAccessToken(overrideConfig);
        }
        clientRef.current.requestCode();
    };
    return React.createElement(tag, {
        className,
        onClick: handleOnLogin,
    }, [children]);
}

function GoogleLoginButton(props) {
    const { loginType, onSuccess, ...otherProps } = props;
    const handleOnSuccess = (response) => {
        if ("credential" in response) {
            return onSuccess({
                tokenType: "id-token",
                token: response.credential,
            });
        }
        if ("code" in response) {
            return onSuccess({
                tokenType: "auth-code",
                token: response.code,
            });
        }
        if ("access_token" in response) {
            return onSuccess({
                tokenType: "access-token",
                token: response.access_token,
            });
        }
    };
    if (loginType === "built-in") {
        const builtInProps = Object.assign({}, otherProps, {
            onSuccess: handleOnSuccess,
        });
        return React.createElement(GoogleBuiltInLoginButton, { ...builtInProps });
    }
    const customProps = Object.assign({}, otherProps, {
        onSuccess: handleOnSuccess,
    });
    return React.createElement(GoogleCustomLoginButton, { ...customProps });
}

export { GoogleLoginButton };
